<script lang="ts" setup>
import { cloneDeep } from 'lodash-es';
import { ref, watch, type PropType } from 'vue';

defineOptions({
  name: 'Pagination',
});

interface PagerData {
  first?: number;
  previous?: number;
  pages?: any;
  next?: number;
  last?: number;
}

const props = defineProps({
  pager: {
    type: Object,
    default() {
      return {
        // 偏移量
        offset: 0,
        // 数据总条数
        total: 0,
      };
    },
  },

  numLinks: {
    type: Number,
    default: 5,
  },

  limit: {
    type: [Number, String],
    default: 20,
  },

  routerMode: {
    type: Boolean,
    default: false,
  },

  setRouterPath: {
    type: Function as PropType<(offset: number) => string>,
    default: null,
  },
});

const emit = defineEmits(['change']);
const listing = ref<any>({});
const totalPage = ref(0);

const refresh = () => {
  const output: PagerData = {};
  const pager = cloneDeep(props.pager);
  let current = (pager.offset || 0) / Number(props.limit) + 1;
  const total = Math.ceil((pager.total || 0) / Number(props.limit));
  totalPage.value = total;

  if (pager.total == 0) {
    listing.value = {};

    return;
  }

  if (current > total) {
    current = total;
  }

  if (total == 1) {
    listing.value = {};

    return;
  }

  const start = ((current - props.numLinks) > 0) ? current - (props.numLinks - 1) : 1;
  const end = ((current + props.numLinks) < total) ? current + props.numLinks : total;

  // 首页
  if (current > (props.numLinks + 1)) {
    output.first = 1;
  }

  // 上一页
  if (current != 1) {
    output.previous = current - 1;
  }

  output.pages = [];

  // 页码
  for (let loop = start - 1; loop <= end; loop++) {
    if (loop >= 1) {
      if (current == loop) {
      // 当前页
        output.pages.push({ number: '', text: loop });
      }
      else {
        output.pages.push({ number: loop, text: loop });
      }
    }
  }

  // 下一页
  if (current < total) {
    output.next = current + 1;
  }

  // 最后一页
  if ((current + props.numLinks) < total) {
    output.last = total;
  }

  listing.value = output;
}

const onPagerClick = (event: Event) => {
  if (props.routerMode) {
    return;
  }

  event.preventDefault();

  const target = event.target as HTMLElement;

  if (target.tagName.toLowerCase() !== 'a') {
    return;
  }

  const pageNumber = target.getAttribute('data-page');

  emit('change', (Number(pageNumber) - 1) * Number(props.limit));
}

const getOffset = (page: number) => (page - 1) * Number(props.limit);

watch(() => props.pager, () => {
  refresh();
});

refresh();
</script>

<template>
  <nav class="page-pagination" aria-label="Page navigation" v-if="typeof listing.pages === 'object' && listing.pages !== null">
    <ul class="pagination m-0 flex list-none items-center justify-center p-0 text-14px color-hex-2d2d2d <md:flex-wrap <md:gap-y-12" @click="onPagerClick">
      <li v-if="listing.first >= 0">
        <template v-if="props.routerMode && !!props.setRouterPath">
          <RouterLink class="block color-hex-2d2d2d no-underline" rel="first" :to="props.setRouterPath(getOffset(listing.first))">首页</RouterLink>
        </template>
        <template v-else>
          <a class="block color-hex-2d2d2d no-underline" href="javascript:;" rel="first" :data-page="listing.first">首页</a>
        </template>
      </li>
      <li v-if="listing.previous >= 0" class="ml-12px">
        <template v-if="props.routerMode && !!props.setRouterPath">
          <RouterLink class="i-home-icons:arrow-left block color-hex-2d2d2d no-underline" rel="prev" :to="props.setRouterPath(getOffset(listing.previous))" />
        </template>
        <template v-else>
          <a class="i-home-icons:arrow-left block color-hex-2d2d2d no-underline" href="javascript:;" rel="prev" :data-page="listing.previous"></a>
        </template>
      </li>
      <template v-for="(item, index) in listing.pages">
        <li v-if="item.number !== ''" :key="index" class="ml-12px">
          <template v-if="props.routerMode && !!props.setRouterPath">
            <RouterLink class="block w-12px text-center color-hex-2d2d2d no-underline" :to="props.setRouterPath(getOffset(item.number))">{{ item.text }}</RouterLink>
          </template>
          <template v-else>
            <a href="javascript:;" class="block w-12px text-center color-hex-2d2d2d no-underline" :data-page="item.number">{{ item.text }}</a>
          </template>
        </li>
        <li class="active ml-12px color-hex-1180FF" :key="`active-${index}`" v-else>
          <span class="block w-12px text-center">{{ item.text }}</span>
        </li>
      </template>
      <li v-if="listing.next >= 0" class="ml-12px">
        <template v-if="props.routerMode && !!props.setRouterPath">
          <RouterLink class="i-home-icons:arrow-right block color-hex-2d2d2d no-underline" rel="next" :to="props.setRouterPath(getOffset(listing.next))" />
        </template>
        <template v-else>
          <a class="i-home-icons:arrow-right block color-hex-2d2d2d no-underline" href="javascript:;" rel="next" :data-page="listing.next"></a>
        </template>
      </li>
      <li v-if="listing.last >= 0" class="ml-12px">
        <template v-if="props.routerMode && !!props.setRouterPath">
          <RouterLink class="block color-hex-2d2d2d no-underline" rel="last" :to="props.setRouterPath(getOffset(listing.last))">末页</RouterLink>
        </template>
        <template v-else>
          <a class="block color-hex-2d2d2d no-underline" href="javascript:;" rel="last" :data-page="listing.last">末页</a>
        </template>
      </li>
    </ul>
  </nav>
</template>
